import React, { useState } from 'react';
import MetadataManager from './common/MetadataManager';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { Dialog, DialogContent, DialogTitle } from '../ui/custom/Dialog';
import {
  tagTitleSuggestionService,
  TagTitleSuggestion
} from '../../services/tagTitleSuggestionService';
import { PlusIcon, TrashIcon, ListBulletIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

interface TagItem {
  id: number;
  name: string;
  description: string | null;
  is_active: boolean;
}

/**
 * Validate that a string is safe (no HTML tags or injection attempts)
 */
function isStringSafe(input: string): boolean {
  if (!input) return true;

  // Check for HTML tags
  if (/<[^>]*>/.test(input)) return false;

  // Check for script injection patterns
  if (/javascript:/i.test(input)) return false;
  if (/on\w+=/i.test(input)) return false;

  return true;
}

export default function TagManagement() {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const [selectedTag, setSelectedTag] = useState<TagItem | null>(null);
  const [isManageTitlesOpen, setIsManageTitlesOpen] = useState(false);
  const [titleSuggestions, setTitleSuggestions] = useState<TagTitleSuggestion[]>([]);
  const [newTitle, setNewTitle] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadTitleSuggestions = async (tagId: number) => {
    try {
      setLoading(true);
      const suggestions = await tagTitleSuggestionService.getTitlesByTagId(tagId);
      setTitleSuggestions(suggestions);
    } catch (error) {
      console.error('Error loading title suggestions:', error);
      toast.error(t('settings.tagManagement.titleLoadError', 'Failed to load title suggestions'));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenManageTitles = (tag: TagItem) => {
    setSelectedTag(tag);
    setIsManageTitlesOpen(true);
    loadTitleSuggestions(tag.id);
  };

  const handleCloseManageTitles = () => {
    setIsManageTitlesOpen(false);
    setSelectedTag(null);
    setTitleSuggestions([]);
    setNewTitle('');
  };

  const handleAddTitle = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTag || !newTitle.trim()) return;

    // Validate for HTML/script injection
    if (!isStringSafe(newTitle)) {
      toast.error(
        t(
          'settings.tagManagement.invalidTitleFormat',
          'Title contains invalid characters or HTML tags'
        )
      );
      return;
    }

    try {
      setSaving(true);
      const created = await tagTitleSuggestionService.createTitleSuggestion(
        selectedTag.id,
        newTitle.trim()
      );
      if (created) {
        setTitleSuggestions([...titleSuggestions, created]);
        setNewTitle('');
        toast.success(t('settings.tagManagement.titleAdded', 'Title suggestion added'));
      } else {
        toast.error(t('settings.tagManagement.titleAddError', 'Failed to add title suggestion'));
      }
    } catch (error) {
      console.error('Error adding title suggestion:', error);
      toast.error(t('settings.tagManagement.titleAddError', 'Failed to add title suggestion'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTitle = async (id: number) => {
    try {
      const deleted = await tagTitleSuggestionService.deleteTitleSuggestion(id);
      if (deleted) {
        setTitleSuggestions(titleSuggestions.filter((s) => s.id !== id));
        toast.success(t('settings.tagManagement.titleDeleted', 'Title suggestion deleted'));
      } else {
        toast.error(
          t('settings.tagManagement.titleDeleteError', 'Failed to delete title suggestion')
        );
      }
    } catch (error) {
      console.error('Error deleting title suggestion:', error);
      toast.error(
        t('settings.tagManagement.titleDeleteError', 'Failed to delete title suggestion')
      );
    }
  };

  const renderExtraActions = (item: TagItem) => (
    <button
      onClick={() => handleOpenManageTitles(item)}
      className={`mr-2 ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-900'}`}
      title={t('settings.tagManagement.titleSuggestions')}
    >
      <ListBulletIcon className="h-5 w-5" />
    </button>
  );

  return (
    <>
      <MetadataManager
        title={t('settings.tagManagement.title')}
        type="tags"
        description={t('settings.tagManagement.description')}
        renderExtraActions={renderExtraActions}
      />

      {/* Manage Title Suggestions Dialog */}
      <Dialog open={isManageTitlesOpen} onOpenChange={setIsManageTitlesOpen}>
        <DialogContent className={`max-w-lg ${isDark ? 'bg-gray-800 border-gray-700' : ''}`}>
          <DialogTitle className={isDark ? 'text-gray-100' : ''}>
            {t('settings.tagManagement.titleSuggestions')} - {selectedTag?.name}
          </DialogTitle>

          <div className="mt-4">
            <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              {t('settings.tagManagement.titleSuggestionsDescription')}
            </p>

            {/* Add new title form */}
            <form onSubmit={handleAddTitle} className="mb-4">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  placeholder={t('settings.tagManagement.titleSuggestionPlaceholder')}
                  className={`flex-1 rounded-md shadow-sm py-2 px-3 text-sm ${
                    isDark
                      ? 'bg-gray-700 border-gray-600 text-gray-100 focus:ring-indigo-500 focus:border-indigo-500'
                      : 'border-gray-300 focus:ring-indigo-500 focus:border-indigo-500'
                  }`}
                />
                <button
                  type="submit"
                  disabled={saving || !newTitle.trim()}
                  className={`inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white ${
                    isDark
                      ? 'bg-indigo-600 hover:bg-indigo-500 disabled:bg-gray-600'
                      : 'bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400'
                  }`}
                >
                  <PlusIcon className="h-4 w-4" />
                </button>
              </div>
            </form>

            {/* Title suggestions list */}
            {loading ? (
              <div className="flex items-center justify-center p-4">
                <div
                  className={`animate-spin rounded-full h-6 w-6 border-b-2 ${
                    isDark ? 'border-blue-400' : 'border-blue-500'
                  }`}
                />
              </div>
            ) : titleSuggestions.length === 0 ? (
              <p
                className={`text-center py-4 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
              >
                {t('settings.tagManagement.noTitleSuggestions')}
              </p>
            ) : (
              <ul className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                {titleSuggestions.map((suggestion) => (
                  <li
                    key={suggestion.id}
                    className={`flex items-center justify-between py-3 px-2 ${
                      isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex-1">
                      <p className={`text-sm ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                        {suggestion.title}
                      </p>
                      {suggestion.usage_count > 0 && (
                        <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          {t('settings.tagManagement.usageCount', {
                            count: suggestion.usage_count
                          })}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => handleDeleteTitle(suggestion.id)}
                      className={
                        isDark
                          ? 'text-red-400 hover:text-red-300'
                          : 'text-red-600 hover:text-red-900'
                      }
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={handleCloseManageTitles}
                className={`px-4 py-2 text-sm font-medium rounded-md ${
                  isDark
                    ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {t('common.close', 'Close')}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
